import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardTitle, 
  Button, 
  ButtonGroup,
  Input,
  FormGroup,
  Label
} from 'reactstrap';
import Chart from 'react-apexcharts';
import axios from 'axios';
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import accessToken from "../../../helpers/jwt-token-access/accessToken";
import CountUp from 'react-countup';
import { 
  TrendingUp, 
  Groups, // or Users
  Description, // or Article
  EmojiEvents, // or Award
  Monitor,
  Smartphone,
  Storage , // or Database
  Headphones
} from '@mui/icons-material';

const ManagementDashboard = () => {
  const [timeFrame, setTimeFrame] = useState('yearly');
  const [timeFrameSub, setTimeFrameSub] = useState('yearly');
  const [dashboardData, setDashboardData] = useState(null);
  const [countCards, setCountCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromDateSub, setFromDateSub] = useState('');
  const [toDateSub, setToDateSub] = useState('');
  const [dateErrors, setDateErrors] = useState({});
  
  const API_URL = process.env.REACT_APP_APIURL || "http://localhost:3099/";

  const today = new Date().toISOString().split('T')[0];

  const validateDates = (from, to, type = 'registration') => {
    const newErrors = { ...dateErrors };
    const errorKey = type === 'registration' ? 'registration' : 'subscription';
    
    if (from && to) {
      if (new Date(from) > new Date(to)) {
        newErrors[errorKey] = 'From date cannot be after To date';
      } else if (new Date(to) > new Date(today)) {
        newErrors[errorKey] = 'To date cannot be in the future';
      } else {
        delete newErrors[errorKey];
      }
    } else {
      delete newErrors[errorKey];
    }
    
    setDateErrors(newErrors);
    return !newErrors[errorKey];
  };

  const setQuickDateRange = (range, type = 'registration') => {
    let from, to;
    const now = new Date();
    
    switch (range) {
      case 'last7days':
        from = new Date(now.setDate(now.getDate() - 7)).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0];
        break;
      case 'last30days':
        from = new Date(now.setDate(now.getDate() - 30)).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0];
        break;
      case 'last3months':
        from = new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0];
        break;
      case 'thisYear':
        from = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    if (type === 'registration') {
      setFromDate(from);
      setToDate(to);
      fetchDashboardData(timeFrame, from, to);
    } else {
      setFromDateSub(from);
      setToDateSub(to);
      fetchSubscriptionData(timeFrameSub, from, to);
    }
  };

  const clearDateFilter = (type = 'registration') => {
    if (type === 'registration') {
      setFromDate('');
      setToDate('');
      fetchDashboardData(timeFrame);
    } else {
      setFromDateSub('');
      setToDateSub('');
      fetchSubscriptionData(timeFrameSub);
    }
    
    const newErrors = { ...dateErrors };
    delete newErrors[type];
    setDateErrors(newErrors);
  };

  const fetchDashboardData = async (timeFrame, from = '', to = '') => {
    try {
      let url = `${API_URL}dashboard/institute-data?timeFrame=${timeFrame}`;
      if (from && to) {
        url += `&from=${from}&to=${to}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          "x-access-token": accessToken,
        }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  };

  const fetchSubscriptionData = async (timeFrameSub, from = '', to = '') => {
    try {
      let url = `${API_URL}dashboard/subscription-data?timeFrameSub=${timeFrameSub}`;
      if (from && to) {
        url += `&from=${from}&to=${to}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          "x-access-token": accessToken,
        }
      });
      setCountCards(response.data);
    } catch (error) {
      console.error('Error fetching subscription datas:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchDashboardData(timeFrame, fromDate, toDate);
  }, [timeFrame]);

  useEffect(() => {
    fetchSubscriptionData(timeFrameSub, fromDateSub, toDateSub);
  }, [timeFrameSub]);

  const handleRegistrationDateChange = (field, value) => {
    if (field === 'from') {
      setFromDate(value);
      validateDates(value, toDate, 'registration');
    } else {
      setToDate(value);
      validateDates(fromDate, value, 'registration');
    }
  };

  const handleSubscriptionDateChange = (field, value) => {
    if (field === 'from') {
      setFromDateSub(value);
      validateDates(value, toDateSub, 'subscription');
    } else {
      setToDateSub(value);
      validateDates(fromDateSub, value, 'subscription');
    }
  };

  const applyRegistrationDateFilter = () => {
    if (fromDate && toDate && validateDates(fromDate, toDate, 'registration')) {
      fetchDashboardData(timeFrame, fromDate, toDate);
    }
  };

  const applySubscriptionDateFilter = () => {
    if (fromDateSub && toDateSub && validateDates(fromDateSub, toDateSub, 'subscription')) {
      fetchSubscriptionData(timeFrameSub, fromDateSub, toDateSub);
    }
  };

  const getChartOptions = () => {
    if (!dashboardData?.data?.registrationData) return {};

    const categories = dashboardData.data.registrationData.map(item => {
      const label = item.periodLabel;
      if (timeFrame === 'daily') {
        return label.split(' ').slice(0, 2).join(' ');
      } else if (timeFrame === 'monthly') {
        return label.split(' ')[0];
      } else {
        return label;
      }
    });

    return {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: [0, 0, 0, 3],
        colors: ['transparent', 'transparent', 'transparent', '#6366f1']
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: '#64748b',
            fontSize: '12px'
          }
        }
      },
      yaxis: [
        {
          title: {
            text: 'Registrations',
            style: {
              color: '#64748b'
            }
          },
          labels: {
            style: {
              colors: '#64748b'
            }
          }
        },
        {
          opposite: true,
          title: {
            text: 'Total',
            style: {
              color: '#6366f1'
            }
          },
          labels: {
            style: {
              colors: '#6366f1'
            }
          }
        }
      ],
      fill: {
        opacity: [0.8, 0.8, 0.8, 0.3],
        colors: ['#f59e0b', '#3b82f6', '#10b981', '#6366f1']
      },
      colors: ['#f59e0b', '#3b82f6', '#10b981', '#6366f1'],
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 10,
        labels: {
          colors: '#64748b'
        }
      },
      grid: {
        borderColor: '#e2e8f0',
        strokeDashArray: 3
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light'
      }
    };
  };

  const getChartSeries = () => {
    if (!dashboardData?.data?.registrationData) return [];

    const { registrationData } = dashboardData.data;

    return [
      {
        name: 'CRM',
        type: 'column',
        data: registrationData.map(item => item.crm)
      },
      {
        name: 'Web',
        type: 'column',
        data: registrationData.map(item => item.web)
      },
      {
        name: 'Mobile',
        type: 'column',
        data: registrationData.map(item => item.mobile)
      },
      {
        name: 'Total',
        type: 'line',
        yAxisIndex: 1,
        data: registrationData.map(item => item.total)
      }
    ];
  };
  
  const getSubscriptionChartOptions = () => {
    if (!countCards?.data?.subscriptionData) return {};
    
    const categories = countCards.data.subscriptionData.map(item => {
      const label = item.periodLabel;
      if (timeFrameSub === 'daily') {
        return label.split(' ').slice(0, 2).join(' ');
      } else if (timeFrameSub === 'monthly') {
        return label.split(' ')[0];
      } else {
        return label;
      }
    });

    return {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: [0, 0, 0, 3],
        colors: ['transparent', 'transparent', 'transparent', '#8b5cf6']
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: '#64748b',
            fontSize: '12px'
          }
        }
      },
      yaxis: [
        {
          title: {
            text: 'Subscriptions',
            style: {
              color: '#64748b'
            }
          },
          labels: {
            style: {
              colors: '#64748b'
            }
          }
        },
        {
          opposite: true,
          title: {
            text: 'Total',
            style: {
              color: '#8b5cf6'
            }
          },
          labels: {
            style: {
              colors: '#8b5cf6'
            }
          }
        }
      ],
      fill: {
        opacity: [0.8, 0.8, 0.8, 0.3],
        colors: ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
      },
      colors: ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 10,
        labels: {
          colors: '#64748b'
        }
      },
      grid: {
        borderColor: '#e2e8f0',
        strokeDashArray: 3
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light'
      }
    };
  };

  const getSubscriptionChartSeries = () => {
    if (!countCards?.data?.subscriptionData) return [];

    const { subscriptionData: subData } = countCards.data;

    return [
      {
        name: 'Resume Wise',
        type: 'column',
        data: subData.map(item => item.resumeWise)
      },
      {
        name: 'Job Post Wise',
        type: 'column',
        data: subData.map(item => item.jobPostWise)
      },
      {
        name: 'Recruit Support',
        type: 'column',
        data: subData.map(item => item.recruitSupport)
      },
      {
        name: 'Total',
        type: 'line',
        yAxisIndex: 1,
        data: subData.map(item => item.total)
      }
    ];
  };

  const CountCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="h-100 shadow-sm border-0">
      <CardBody className="d-flex justify-content-between align-items-center flex-wrap">
        <div className="d-flex align-items-center mb-2 mb-md-0">
          <div className={`p-2 rounded-circle me-3 bg-${color}-100`}>
            <Icon size={32} className={`text-${color}-600`} style={{ width: '32px', height: '32px' }} />
          </div>
          <div>
            <h6 className="mb-0 text-muted large">{title}</h6>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
        </div>
        <div>
          <h2 className="mb-0 fw-bold text-end">
            <CountUp 
              end={value} 
              duration={2} 
              separator="," 
            />
          </h2>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid className="p-2 bg-light min-vh-100">
          {/* Header */}
          <Breadcrumbs
            title="Indiana Global Teachers"
            breadcrumbItem="Management Dashboard"
          />

          {/* Count Cards */}
          <Row className="mb-2">
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Total Subscriptions"
                value={countCards?.data?.summary?.totals?.total || 0}
                icon={EmojiEvents}
                color="primary"
              />
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Resume Wise"
                value={countCards?.data?.summary?.totals?.resumeWise || 0}
                icon={Description}
                color="success"
              />
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Job Post Wise"
                value={countCards?.data?.summary?.totals?.jobPostWise || 0}
                icon={TrendingUp}
                color="warning"
              />
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Recruit with Support"
                value={countCards?.data?.summary?.totals?.recruitSupport || 0}
                icon={Headphones}
                color="info"
              />
            </Col>
          </Row>

          {/* Summary Cards */}
          <Row className='mb-3'>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Total Management Registrations"
                value={dashboardData?.data?.summary?.totals?.total || 0}
                icon={Groups}
                color="primary"
              />
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="CRM Registrations"
                value={dashboardData?.data?.summary?.totals?.crm || 0}
                icon={Storage}
                color="warning"
              />
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Web Registrations"
                value={dashboardData?.data?.summary?.totals?.web || 0}
                icon={Monitor}
                color="success"
              />
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <CountCard 
                title="Mobile Registrations"
                value={dashboardData?.data?.summary?.totals?.mobile || 0}
                icon={Smartphone}
                color="info"
              />
            </Col>
          </Row>

          {/* Chart Section */}
          <Row className='mb-4'>
            {/* Registration Analytics */}
            <Col lg={6}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <CardTitle tag="h5" className="mb-0">Registration Analytics</CardTitle>
                      <small className="text-muted">
                        Registration trends by source with total overview
                      </small>
                    </div>
                    <ButtonGroup size="sm">
                      <Button 
                        color={timeFrame === 'daily' ? 'primary' : 'outline-primary'}
                        onClick={() => setTimeFrame('daily')}
                      >
                        Daily
                      </Button>
                      <Button 
                        color={timeFrame === 'monthly' ? 'primary' : 'outline-primary'}
                        onClick={() => setTimeFrame('monthly')}
                      >
                        Monthly
                      </Button>
                      <Button 
                        color={timeFrame === 'yearly' ? 'primary' : 'outline-primary'}
                        onClick={() => setTimeFrame('yearly')}
                      >
                        Yearly
                      </Button>
                    </ButtonGroup>
                  </div>

                  {/* Registration Date Filters */}
                  <Card className="mb-3 bg-light border-0">
                    <CardBody className="py-2">
                      {/* <h6 className="mb-2 text-muted">Date Filter - Registrations</h6> */}
                      
                      {/* Quick Filters */}
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('last7days', 'registration')}>
                          Last 7 Days
                        </Button>
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('last30days', 'registration')}>
                          Last 30 Days
                        </Button>
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('last3months', 'registration')}>
                          Last 3 Months
                        </Button>
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('thisYear', 'registration')}>
                          This Year
                        </Button>
                      </div>

                      {/* Custom Date Range */}
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label for="regFromDate" className="small">From Date</Label>
                            <Input
                              type="date"
                              id="regFromDate"
                              size="sm"
                              value={fromDate}
                              onChange={(e) => handleRegistrationDateChange('from', e.target.value)}
                              max={today}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label for="regToDate" className="small">To Date</Label>
                            <Input
                              type="date"
                              id="regToDate"
                              size="sm"
                              value={toDate}
                              onChange={(e) => handleRegistrationDateChange('to', e.target.value)}
                              max={today}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                          <div className="d-flex gap-2">
                            <Button size="sm" color="primary" onClick={applyRegistrationDateFilter} disabled={!fromDate || !toDate}>
                              Apply
                            </Button>
                            <Button size="sm" color="secondary" onClick={() => clearDateFilter('registration')}>
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                      
                      {dateErrors.registration && (
                        <div className="text-danger small mt-1">
                          {dateErrors.registration}
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  <Chart
                    options={getChartOptions()}
                    series={getChartSeries()}
                    type="line"
                    height={400}
                  />
                </CardBody>
              </Card>
            </Col>

            {/* Subscription Analytics */}
            <Col lg={6}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <CardTitle tag="h5" className="mb-0">Subscription Analytics</CardTitle>
                      <small className="text-muted">
                        Subscription trends by source with total overview
                      </small>
                    </div>
                    <ButtonGroup size="sm">
                      <Button 
                        color={timeFrameSub === 'daily' ? 'primary' : 'outline-primary'}
                        onClick={() => setTimeFrameSub('daily')}
                      >
                        Daily
                      </Button>
                      <Button 
                        color={timeFrameSub === 'monthly' ? 'primary' : 'outline-primary'}
                        onClick={() => setTimeFrameSub('monthly')}
                      >
                        Monthly
                      </Button>
                      <Button 
                        color={timeFrameSub === 'yearly' ? 'primary' : 'outline-primary'}
                        onClick={() => setTimeFrameSub('yearly')}
                      >
                        Yearly
                      </Button>
                    </ButtonGroup>
                  </div>

                  {/* Subscription Date Filters */}
                  <Card className="mb-3 bg-light border-0">
                    <CardBody className="py-2">
                      {/* <h6 className="mb-2 text-muted">Date Filter - Subscriptions</h6> */}
                      
                      {/* Quick Filters */}
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('last7days', 'subscription')}>
                          Last 7 Days
                        </Button>
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('last30days', 'subscription')}>
                          Last 30 Days
                        </Button>
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('last3months', 'subscription')}>
                          Last 3 Months
                        </Button>
                        <Button size="sm" color="outline-secondary" onClick={() => setQuickDateRange('thisYear', 'subscription')}>
                          This Year
                        </Button>
                      </div>

                      {/* Custom Date Range */}
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label for="subFromDate" className="small">From Date</Label>
                            <Input
                              type="date"
                              id="subFromDate"
                              size="sm"
                              value={fromDateSub}
                              onChange={(e) => handleSubscriptionDateChange('from', e.target.value)}
                              max={today}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label for="subToDate" className="small">To Date</Label>
                            <Input
                              type="date"
                              id="subToDate"
                              size="sm"
                              value={toDateSub}
                              onChange={(e) => handleSubscriptionDateChange('to', e.target.value)}
                              max={today}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                          <div className="d-flex gap-2">
                            <Button size="sm" color="primary" onClick={applySubscriptionDateFilter} disabled={!fromDateSub || !toDateSub}>
                              Apply
                            </Button>
                            <Button size="sm" color="secondary" onClick={() => clearDateFilter('subscription')}>
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                      
                      {dateErrors.subscription && (
                        <div className="text-danger small mt-1">
                          {dateErrors.subscription}
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  <Chart
                    options={getSubscriptionChartOptions()}
                    series={getSubscriptionChartSeries()}
                    type="line"
                    height={400}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ManagementDashboard;